import type { TokenValueLastChange, TokenValueMetadata } from './types/token';

/**
 * Parses token value provenance from an unknown payload.
 *
 * Accepts both an object and a JSON string, since BlockNote custom inline
 * schemas only preserve primitive props and provenance round-trips as JSON.
 * Example payload:
 * {"isAutoFilled":false,"authorId":"user-1","lastChange":{"changeKind":"edited",
 *  "oldValue":"60 bpm","newValue":"72 bpm","changedAt":"2026-06-04T10:00:00Z",
 *  "changedBy":"user-1","changedByName":"Dora Osimani"}}
 */
export function parseTokenValueMetadata(
  value: unknown,
): TokenValueMetadata | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    try {
      return parseTokenValueMetadata(JSON.parse(trimmed) as unknown);
    } catch {
      return undefined;
    }
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const metadata = value as {
    isAutoFilled?: unknown;
    autoFilledAt?: unknown;
    authorId?: unknown;
    lastChange?: unknown;
  };

  return {
    isAutoFilled: metadata.isAutoFilled === true,
    autoFilledAt:
      typeof metadata.autoFilledAt === 'string'
        ? metadata.autoFilledAt
        : undefined,
    authorId:
      typeof metadata.authorId === 'string' ? metadata.authorId : undefined,
    lastChange: parseTokenValueLastChange(metadata.lastChange),
  };
}

/**
 * Parses the audit payload describing the most recent token value change.
 * Entries without a changeKind are treated as absent.
 */
export function parseTokenValueLastChange(
  value: unknown,
): TokenValueLastChange | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const lastChange = value as {
    changeKind?: unknown;
    oldValue?: unknown;
    newValue?: unknown;
    changedAt?: unknown;
    changedBy?: unknown;
    changedByName?: unknown;
  };

  if (typeof lastChange.changeKind !== 'string' || !lastChange.changeKind) {
    return undefined;
  }

  return {
    changeKind: lastChange.changeKind,
    oldValue:
      typeof lastChange.oldValue === 'string' ? lastChange.oldValue : undefined,
    newValue:
      typeof lastChange.newValue === 'string' ? lastChange.newValue : undefined,
    changedAt:
      typeof lastChange.changedAt === 'string'
        ? lastChange.changedAt
        : undefined,
    changedBy:
      typeof lastChange.changedBy === 'string'
        ? lastChange.changedBy
        : undefined,
    changedByName:
      typeof lastChange.changedByName === 'string'
        ? lastChange.changedByName
        : undefined,
  };
}
